import { HARDENING_CLUSTERS } from '@/lib/constants/hardening'

export { MDM_PROFILE_DEPLOYMENT_STATUS, MDM_PROFILE_HOST_STATUS } from '@/lib/constants/status'

export const MDM_CONFIG_PROFILE_PLATFORM = {
  MACOS: 'macOS',
  WINDOWS: 'Windows',
  IOS: 'iOS',
  ANDROID: 'Android',
} as const

export const MDM_PROFILE_TYPE = {
  PREDEFINED: 'predefined',
  CUSTOM: 'custom',
  /** Profile owned by the OS Updates tab, hidden from the Custom Policies list. */
  OS_UPDATES: 'os_updates',
  /** Built-in CIS benchmark profile, defined in code and kept in sync for every client. */
  CIS: 'cis',
} as const

/** The CIS benchmark's checks (the EDR's own), in the order screens list them: the security baseline of every host. */
export const MDM_CIS_CHECKS = [
  HARDENING_CLUSTERS.PASSWORD_SECURITY,
  HARDENING_CLUSTERS.DISK_ENCRYPTION,
  HARDENING_CLUSTERS.SCREEN_LOCK,
  HARDENING_CLUSTERS.PRIVILEGE_PROTECTION,
  HARDENING_CLUSTERS.HOST_FIREWALL,
]

/** A baseline check with no reading yet: no EDR result and no CIS profile reported for the host. */
export const MDM_CHECK_NOT_CHECKED = 'not-checked'

/** Where a policy stands on its latest deployment, as the policy lists show it. */
export const MDM_POLICY_STATUS = {
  NOT_DEPLOYED: 'not_deployed',
  PENDING: 'pending',
  FAILED: 'failed',
  DEPLOYED: 'deployed',
} as const

/** Made by Qalea and read-only for the client: the Qalea library (predefined) and the CIS benchmark. */
export const MDM_BUILT_IN_PROFILE_TYPES: string[] = [MDM_PROFILE_TYPE.PREDEFINED, MDM_PROFILE_TYPE.CIS]

/** Android Management API `systemUpdate.type`. Fully-managed devices only, ignored on BYOD. */
export const ANDROID_SYSTEM_UPDATE_TYPE = {
  AUTOMATIC: 'AUTOMATIC',
  WINDOWED: 'WINDOWED',
  POSTPONE: 'POSTPONE',
} as const

export type IAndroidSystemUpdateType = (typeof ANDROID_SYSTEM_UPDATE_TYPE)[keyof typeof ANDROID_SYSTEM_UPDATE_TYPE]

// LATEST is also Fleet's minimum_version value for "newest version each device supports". Windows has no SPECIFIC.
export const OS_UPDATE_TARGET = {
  NONE: 'none',
  SPECIFIC: 'specific',
  LATEST: 'latest',
} as const

export type IOsUpdateTarget = (typeof OS_UPDATE_TARGET)[keyof typeof OS_UPDATE_TARGET]

export const APPLE_OS_UPDATE_LATEST_DEFAULT_DEADLINE_DAYS = 3

// Fleet's reason when Apple no longer (or not yet) lists the minimum version
export const FLEET_APPLE_VERSION_UNSUPPORTED_REASON = "The minimum version isn't supported by Apple."

export const ANDROID_OS_UPDATES_PROFILE_NAME = 'Android OS updates'

export const DISK_ENCRYPTION_STATUS = {
  VERIFIED: 'verified',
  VERIFYING: 'verifying',
  ACTION_REQUIRED: 'action_required',
  ENFORCING: 'enforcing',
  FAILED: 'failed',
  REMOVING_ENFORCEMENT: 'removing_enforcement',
} as const

export const DISK_ENCRYPTION_STATUSES = Object.values(DISK_ENCRYPTION_STATUS)

export const HOST_COMPLIANCE_STATE = {
  PASS: 'pass' as const,
  FAIL: 'fail' as const,
  PENDING: 'pending' as const,
  ACTION_REQUIRED: 'action_required' as const,
  NOT_APPLICABLE: 'not_applicable' as const,
} as const

export type IHostComplianceState = (typeof HOST_COMPLIANCE_STATE)[keyof typeof HOST_COMPLIANCE_STATE]

export const MDM_PROFILE_FORMATS: Record<string, string[]> = {
  macOS: ['.mobileconfig', '.json'],
  iOS: ['.mobileconfig', '.json'],
  Windows: ['.xml'],
  Android: ['.json'],
}
